/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import AdminCoach from 'bundles/admin-dashboard/components/AdminCoach';
import { MoreVerticalFilledIcon } from 'bundles/ai-course-coach/assets/mui';
import EnterpriseAdminSideNavigation from 'bundles/enterprise-admin-navigation/components/side-nav/EnterpriseAdminSideNavigation';
import { EnterpriseAdminCoachProvider } from 'bundles/enterprise-admin/EnterpriseAdminCoachContext';
import { useEnterpriseAdminContext } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

const styles = {
  resizeHandle: css`
    background-color: var(--cds-color-neutral-background-primary-weak);
    display: flex;
    justify-content: center;
    align-items: center;
    border-left: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    color: var(--cds-color-grey-700);
    transition: color 0.2s, background-color 0.2s;

    :hover,
    :focus {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,
  coach: css`
    padding-left: 0;
    ${breakpoints.up('sm')} {
      z-index: auto;
    }
  `,
  fixedCoach: css`
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
  `,
  sideNavShell: css`
    display: grid;
    grid-template-columns: auto 1fr;
    height: 100vh;
    overflow: hidden;
  `,
  sideNavContentCell: css`
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  `,
};

const WithGlobalSideNavAndStyleGuideContent: React.LegacyFunctionComponentWithChildren<{}> = ({
  children,
  ...props
}) => {
  const org = useThirdPartyOrganization();
  const { adminRoleType } = useEnterpriseAdminContext();
  const [showCoach, setShowCoach] = React.useState(false);
  const isDesktop = useMediaQuery(breakpoints.up('sm'));

  const toggleCoach = React.useCallback<() => void>(() => {
    setShowCoach((prev) => !prev);
  }, []);

  return (
    <div className="styleguide" css={styles.sideNavShell}>
      <EnterpriseAdminSideNavigation
        thirdPartyOrganization={org}
        adminRoleType={adminRoleType}
        toggleCoach={toggleCoach}
      />
      <div css={styles.sideNavContentCell}>
        {/* Explicit IDs added for SSR hydration stability - prevents "No group found for id" errors */}
        <PanelGroup id="admin-dashboard-panel-group" direction="horizontal" style={{ height: '100%' }}>
          <Panel
            id="admin-dashboard-main-panel"
            style={{ overflow: 'auto', height: '100%', position: 'relative', scrollbarGutter: 'stable' }}
          >
            {React.isValidElement(children) ? React.cloneElement(children, props) : children}
          </Panel>

          {showCoach && isDesktop && (
            <>
              <PanelResizeHandle id="admin-dashboard-resize-handle" css={styles.resizeHandle}>
                <MoreVerticalFilledIcon size={16} />
              </PanelResizeHandle>
              <Panel
                id="admin-dashboard-coach-panel"
                minSize={25}
                defaultSize={28}
                maxSize={50}
                style={{
                  position: 'relative',
                  backgroundColor: 'var(--cds-color-neutral-background-primary-weak)',
                }}
              >
                <AdminCoach thirdPartyOrganization={org} toggleCoach={toggleCoach} style={styles.coach} />
              </Panel>
            </>
          )}
        </PanelGroup>
      </div>

      {/* Mobile coach rendered outside PanelGroup - only Panel/PanelResizeHandle can be direct children */}
      {showCoach && !isDesktop && (
        <AdminCoach thirdPartyOrganization={org} toggleCoach={toggleCoach} style={styles.fixedCoach} />
      )}
    </div>
  );
};

const WithGlobalSideNavAndStyleGuide: React.FC<React.PropsWithChildren<{}>> = ({ children, ...props }) => {
  return (
    <EnterpriseAdminCoachProvider>
      <WithGlobalSideNavAndStyleGuideContent {...props}>{children}</WithGlobalSideNavAndStyleGuideContent>
    </EnterpriseAdminCoachProvider>
  );
};

export default WithGlobalSideNavAndStyleGuide;
